import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { MessageForwarded } from "@/components/ui/message/message-forwarded"
import {
  MessageReply,
  MessageReplyFallback,
} from "@/components/ui/message/message-reply"
import { MessageBody } from "@/components/ui/message/message-body"
import { detectType, Message } from "@/components/ui/message"
import { useMessageData } from "@/components/chat/useMessageData"
import useReplyMessage from "@/components/chat/useReplyMessage"
import { Message as MessageType } from "@/reducers/messagesSlice"
import { ScrollArea } from "@/components/ui/scroll-area"
import { DriverWorkspaceProvider } from "@/contexts/DriverWorkspaceContext"

type DeleteMessageDialogProps = {
  open: boolean
  message: MessageType
  onClose: () => void
  onDelete: () => Promise<void>
}

const DeleteMessageDialog = ({
  open,
  message,
  onClose,
  onDelete,
}: DeleteMessageDialogProps) => {
  const { t } = useTranslation()
  const handleClose = () => {
    onClose()
    setTimeout(() => {
      document.body.style.pointerEvents = ""
    }, 500)
  }

  const confirmMessageDelete = async () => {
    await onDelete()
    handleClose()
  }

  const messageData = useMessageData(message)
  const type = detectType(message?.fileName)

  const replyMessage = useReplyMessage(message)

  const containsReactions = messageData.reactions.length > 0
  const containsReplyMessage = Boolean(message?.replyMessage)
  const containsForwardedMessage = Boolean(message?.forwarded)
  const containsFallbackReplyMessage = message?.replyMessage && !replyMessage

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("chat.deleteMessage.title")}</DialogTitle>
          <DialogDescription>
            {t("chat.deleteMessage.description")}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-96 overflow-auto">
          {/* A preview in a dialog is not a row in a thread, so it is outside
              the thread layout and keeps the plain row spacing — whether or not
              the dialog was opened from inside the driver workspace. Saying that
              here, rather than overriding the margin, is what keeps the preview
              identical for a customer without the workspace. */}
          {message && (
            <DriverWorkspaceProvider value={false}>
              <Message message={message} replyMessage={replyMessage}>
                <Message.Avatar
                  src={messageData.avatarSrc}
                  name={messageData.authorName}
                  fallback={messageData.authorInitials}
                  avatarColor={messageData.avatarColor}
                />

                <Message.Bubble withDriver={messageData.isFromDriver}>
                  <Message.Header
                    author={messageData.authorName}
                    timestamp={new Date(message.timestamp)}
                    showAuthor={true}
                  ></Message.Header>
                  {containsForwardedMessage && <MessageForwarded />}
                  {containsReplyMessage && <MessageReply />}
                  {containsFallbackReplyMessage && <MessageReplyFallback />}
                  <MessageBody type={type} />
                  <Message.Metadata />
                  {containsReactions && (
                    <Message.Reactions reactions={messageData.reactions} />
                  )}
                </Message.Bubble>
              </Message>
            </DriverWorkspaceProvider>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={confirmMessageDelete}>
            {t("chat.deleteMessage.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DeleteMessageDialog
